import { Typography } from '@mui/material';
import i18n from 'i18n';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { setPaymentMode } from 'store/ticket-service/ticketSlice';
import {
	createFreeSubscription,
	getActiveSubscription,
	getProducts,
	updateSubscriptionPreview,
} from 'store/user-service/asyncActions';
import { getStripeConnection } from 'store/user-service/selector';
import { setStripeConnection } from 'store/user-service/userSlice';

import { getEnvVarAsBoolean } from 'core/helpers/env-helper';
import { hub_events } from 'core/constants/hubEvents';
import { NamespacesList } from 'core/enums/common';
import { useGetConnectionId } from 'core/hooks/useGetConnectionId';
import { useGetUser } from 'core/hooks/useGetUser';
import { TariffPlanType } from 'core/types/base';

import { SharedModal } from 'components/shared/Modal';

import { TariffUpgradeConfirmationModal } from './TariffUpgradeConfirmation';
import * as Styled from './styled';

interface TariffPlansModalProps {
	isOpen: boolean;
	onClose: () => void;
	isOnPurchase: boolean;
}

export const TariffPlansModal: FC<TariffPlansModalProps> = ({ isOpen, onClose, isOnPurchase }) => {
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);
	const [tariffs, setTariffs] = useState([]);
	const [activeTariff, setActiveTariff] = useState<TariffPlanType | null>(null);
	const [tariffsUpdate, setTariffsUpdate] = useState(false);
	const [choosedPlan, setChoosedPlan] = useState<{
		priceId: string;
		previewPrice: number;
	}>({
		priceId: '',
		previewPrice: 0,
	});
	const [gettingPriceLoading, setGettingPriceLoading] = useState(false);
	const [isConfirmUpgrade, setIsConfirmUpgrade] = useState(false);

	const { user } = useGetUser();
	const { connectionId } = useGetConnectionId();

	const connectingStripe = useSelector(getStripeConnection);

	const handleActionConfirmUpgrade = () => {
		setIsConfirmUpgrade((prev) => !prev);
	};

	const dispatch = useAppDispatch();

	const handleGetActiveTariff = async () => {
		if (!getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true)) return;
		
		const activeTariff = await dispatch(getActiveSubscription()).unwrap();
		setActiveTariff(activeTariff);
	};

	const handleGetTariffs = async () => {
		setTariffsUpdate(true);
		const tariffs = await dispatch(getProducts()).unwrap();
		await handleGetActiveTariff();
		setTariffsUpdate(false);
		setTariffs(tariffs);
	};

	const handleCreateFreeSubscription = async () => {
		dispatch(setStripeConnection(true));

		if (connectionId) {
			try {
				await dispatch(
					createFreeSubscription({
						connectionId,
						signalMethodName: hub_events.action.freeSubscription,
					})
				).unwrap();
			} catch (error: any) {
				console.log(error);
			}
		}
	};

	useEffect(() => {
		if (user && isOpen) {
			handleGetTariffs();
		}
	}, [user, isOpen]);

	const getPlanInfo = useCallback(
		(planInfo: string) => {
			if (planInfo && planInfo.includes('/')) {
				const info = planInfo.split('/');
				if (i18n.language === 'en') {
					return info[0];
				}
				if (i18n.language === 'ar') {
					return info[1];
				}
			}
			return planInfo;
		},
		[i18n.language]
	);

	const handleCloseModal = () => {
		onClose();
		if (isOnPurchase) dispatch(setPaymentMode(true));
		setChoosedPlan({
			priceId: '',
			previewPrice: 0,
		});
		setTariffs([]);
	};

	const handleChoosePlan = async (plan: TariffPlanType) => {
		await setChoosedPlan((prev) => ({ ...prev, priceId: plan.priceId }));
		if (!plan.name.includes('Free')) {
			setGettingPriceLoading(true);
			try {
				const previewPrice = await dispatch(
					updateSubscriptionPreview({ newPriceId: plan.priceId })
				).unwrap();
				await setChoosedPlan((prev) => ({ ...prev, previewPrice }));
				handleActionConfirmUpgrade();
			} catch (error) {
				console.log(error);
			} finally {
				setGettingPriceLoading(false);
			}
		} else {
			handleCreateFreeSubscription();
		}
	};

	const getIsLoadingButtonDisabled = useCallback(
		(tariff: TariffPlanType) =>
			(connectingStripe || gettingPriceLoading) && choosedPlan?.priceId === tariff.priceId,
		[choosedPlan, connectingStripe, gettingPriceLoading]
	);

	const getIsTariffButtonDisabled = useCallback(
		(tariff: TariffPlanType, index: number) =>
			tariff.name === activeTariff?.name ||
			index < (user?.accountPlan || 0) ||
			activeTariff?.subscriptionStatus === 1 ||
			activeTariff?.subscriptionStatus === 2 ||
			activeTariff?.subscriptionStatus === 3,
		[activeTariff, user]
	);

	return (
		<SharedModal
			isOpen={isOpen}
			handleClose={handleCloseModal}
			sx={{ width: '100% !important', maxWidth: '1170px' }}
		>
			<Styled.ModalContent>
				<Styled.ModalInfo>
					<Typography variant="h4">{profileTranslate('text.tariffPlans')}</Typography>
					<Typography>{profileTranslate('text.chooseTheRightPricingPlan')}</Typography>
				</Styled.ModalInfo>
				{tariffsUpdate ? (
					<Styled.LoaderBox>
						<Oval
							height={34}
							width={34}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Styled.LoaderBox>
				) : (
					<Styled.Tariffs>
						{tariffs
							.sort((prev: TariffPlanType, next: TariffPlanType) => +prev.price - +next.price)
							.map((tariff: TariffPlanType, index) => (
								<Styled.Tariff
									key={tariff.name}
									sx={{
										'&:last-of-type': {
											height: tariff.name.includes('organizationsTitle') ? 'auto' : '100%',
											minHeight: tariff.name.includes('organizationsTitle') ? 'unset' : '525px',
										},
									}}
								>
									<Styled.TariffInfo>
										<Styled.TariffTitle>{getPlanInfo(tariff.name)}</Styled.TariffTitle>
										<Styled.TariffPrice>
											{tariff.price}$ <span>/{profileTranslate('text.month')}</span>
										</Styled.TariffPrice>
										<Styled.TariffDescription>
											{getPlanInfo(tariff.description)}
										</Styled.TariffDescription>
									</Styled.TariffInfo>
									<Styled.TariffLine />
									<Styled.TariffOpportunities>
										{tariff.features &&
											tariff.features.map((element, index) => (
												<Styled.TariffOpportunity key={`${element}${index}`}>
													{getPlanInfo(element)}
												</Styled.TariffOpportunity>
											))}
									</Styled.TariffOpportunities>
									{getIsLoadingButtonDisabled(tariff) ? (
										<Styled.TariffButton disabled={getIsLoadingButtonDisabled(tariff)}>
											<Oval
												height={24}
												width={24}
												color="#fff"
												wrapperStyle={{}}
												wrapperClass=""
												visible={true}
												ariaLabel="oval-loading"
												secondaryColor="#91B6FF"
												strokeWidth={5}
												strokeWidthSecondary={5}
											/>
										</Styled.TariffButton>
									) : (
										<Styled.TariffButton
											disabled={getIsTariffButtonDisabled(tariff, index)}
											onClick={() => handleChoosePlan(tariff)}
										>
											{profileTranslate(
												tariff.name === activeTariff?.name ? 'text.currentPlan' : 'text.choosePlan'
											)}
										</Styled.TariffButton>
									)}
								</Styled.Tariff>
							))}
					</Styled.Tariffs>
				)}
			</Styled.ModalContent>
			<TariffUpgradeConfirmationModal
				open={isConfirmUpgrade}
				handleClose={handleActionConfirmUpgrade}
				choosedPlan={choosedPlan}
				handleGetActiveTariff={handleGetActiveTariff}
			/>
		</SharedModal>
	);
};
